import { useState, useEffect, useRef } from 'react';
import api from '@/lib/api';
import MaterialSelect from '@/components/MaterialSelect';
import VendorSelect from '@/components/VendorSelect';
import AttachmentPreview from '@/components/AttachmentPreview';
import { Plus, Trash2, Download, Image as ImageIcon, X, Layers } from 'lucide-react';
import { toast } from 'sonner';
import { useConfirm } from '@/components/ConfirmDialog';

const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

const isValidPhone = (s) => !s || /^[+\d][\d\s\-()]{4,}$/.test(s);

export default function ProjectMaterials({ projectId, projectName, projectType, materialsData, onUpdate }) {
  const [confirm, confirmDialog] = useConfirm();
  const [rows, setRows] = useState(materialsData || []);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const fileRefs = useRef({});

  useEffect(() => { setRows(materialsData || []); }, [materialsData]);

  const addRow = () => {
    const draftId = `draft-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    const draft = {
      id: draftId, _draft: true,
      item_name: '', company: '', item_code: '',
      vendor_name: '', vendor_phone: '', attachments: [], notes: ''
    };
    setRows(prev => [...prev, draft]);
  };

  // Persist a field. If the row is still a draft, item_name must be present;
  // a successful first POST upgrades the draft to a real row.
  const persistUpdate = async (rowId, patch) => {
    const row = rows.find(r => r.id === rowId);
    if (!row) return;
    if (row._draft) {
      const merged = { ...row, ...patch };
      if (!merged.item_name?.trim()) {
        // Just hold the patch in local state until item_name is present.
        const updated = rows.map(r => r.id === rowId ? merged : r);
        setRows(updated);
        return;
      }
      try {
        const res = await api.post(`/projects/${projectId}/materials`, {
          item_name: merged.item_name,
          material_id: merged.material_id,
          company: merged.company,
          item_code: merged.item_code,
          vendor_name: merged.vendor_name,
          vendor_phone: merged.vendor_phone,
          attachments: merged.attachments,
          notes: merged.notes,
        });
        const real = res.data;
        const updated = rows.map(r => r.id === rowId ? real : r);
        setRows(updated); onUpdate?.(updated);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Save failed');
      }
      return;
    }
    try {
      await api.put(`/projects/${projectId}/materials/${rowId}`, patch);
      const updated = rows.map(r => r.id === rowId ? { ...r, ...patch } : r);
      setRows(updated); onUpdate?.(updated);
    } catch { toast.error('Update failed'); }
  };

  const deleteRow = async (rowId) => {
    const row = rows.find(r => r.id === rowId);
    if (!row) return;
    if (row._draft) {
      // Drop draft locally - never persisted.
      setRows(rows.filter(r => r.id !== rowId));
      return;
    }
    if (!(await confirm({ title: 'Delete row?', message: 'This material row and its attachments will be removed.', confirmLabel: 'Delete' }))) return;
    try {
      await api.delete(`/projects/${projectId}/materials/${rowId}`);
      const updated = rows.filter(r => r.id !== rowId);
      setRows(updated); onUpdate?.(updated);
    } catch { toast.error('Delete failed'); }
  };

  const onItemChange = async (rowId, name, material) => {
    const patch = { item_name: name };
    if (material?.id) patch.material_id = material.id;
    await persistUpdate(rowId, patch);
  };

  const onVendorChange = async (rowId, name, vendor) => {
    const patch = { vendor_name: name };
    if (vendor) {
      patch.vendor_id = vendor.id || null;
      patch.vendor_phone = vendor.phone || '';
    } else {
      // cleared selection
      patch.vendor_id = null;
      patch.vendor_phone = '';
    }
    await persistUpdate(rowId, patch);
  };

  const onAttach = async (rowId, files) => {
    if (!files?.length) return;
    const file = files[0];
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Only JPG, PNG, WEBP allowed'); return;
    }
    if (file.size > 4 * 1024 * 1024) {
      toast.error('Max 4MB'); return;
    }
    try {
      const dataUrl = await readFileAsDataUrl(file);
      const att = {
        id: crypto.randomUUID?.() || Math.random().toString(36).slice(2),
        name: file.name,
        size: file.size,
        type: file.type,
        data_url: dataUrl
      };
      const row = rows.find(r => r.id === rowId);
      const next = [...(row?.attachments || []), att];
      await persistUpdate(rowId, { attachments: next });
      toast.success('Photo attached');
    } catch { toast.error('Upload failed'); }
  };

  const removeAttachment = async (rowId, attId) => {
    const row = rows.find(r => r.id === rowId);
    const next = (row?.attachments || []).filter(a => a.id !== attId);
    await persistUpdate(rowId, { attachments: next });
  };

  const exportExcel = () => {
    import('xlsx').then(XLSX => {
      import('file-saver').then(({ saveAs }) => {
        const data = rows.map(r => ({
          'Item Name': r.item_name || '',
          'Company': r.company || '',
          'Item Code': r.item_code || '',
          'Vendor Name': r.vendor_name || '',
          'Vendor Contact': r.vendor_phone || '',
          'Attachments': (r.attachments || []).length,
          'Notes': r.notes || '',
        }));
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Materials');
        const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        saveAs(new Blob([buf]), `Materials-${projectName || 'Project'}.xlsx`);
        toast.success('Exported');
      });
    });
  };

  const InlineText = ({ rowId, field, value, placeholder, className = '', testId, validate }) => {
    const [v, setV] = useState(value || '');
    useEffect(() => { setV(value || ''); }, [value]);
    const invalid = validate && v && !validate(v);
    return (
      <input
        type="text"
        value={v}
        onChange={e => setV(e.target.value)}
        onBlur={() => { if (v !== (value || '')) persistUpdate(rowId, { [field]: v }); }}
        onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
        placeholder={placeholder}
        className={`w-full h-9 px-2.5 text-xs bg-transparent border border-transparent rounded-lg hover:border-[#E8E6E1] focus:outline-none focus:border-[#3A8C8C] focus:bg-white transition-colors ${invalid ? 'border-[#C4727F]' : ''} ${className}`}
        data-testid={testId}
      />
    );
  };

  return (
    <div className="space-y-4" data-testid="project-materials">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="section-heading text-sm font-semibold font-['Manrope'] flex items-center gap-2">
            <Layers size={14} strokeWidth={1.5} className="text-[#3A8C8C]" /> Materials
          </h4>
          <p className="text-[11px] text-[#6B7280] mt-0.5">{rows.length} {rows.length === 1 ? 'item' : 'items'} · linked to Material List + Vendor Master</p>
        </div>
        <div className="flex gap-2">
          <button onClick={exportExcel} disabled={!rows.length}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#E8E6E1] text-[#2C2C2E] text-xs font-medium hover:bg-[#D4D0C8] transition-colors disabled:opacity-40"
            data-testid="materials-export">
            <Download size={13} /> Export
          </button>
          <button onClick={addRow}
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-[#3A8C8C] text-white text-xs font-medium hover:bg-[#2E7A7A] transition-colors"
            data-testid="materials-add-row">
            <Plus size={14} /> Add Row
          </button>
        </div>
      </div>

      <div className="glass-card rounded-2xl overflow-hidden" data-testid="materials-grid">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1100px]">
            <thead>
              <tr className="bg-[#FAFAF8] border-b border-[#E8E6E1]">
                <th className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold text-left px-3 py-2.5 w-[26%]">Item Name *</th>
                <th className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold text-left px-3 py-2.5 w-[14%]">Company</th>
                <th className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold text-left px-3 py-2.5 w-[12%]">Item Code</th>
                <th className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold text-left px-3 py-2.5 w-[20%]">Vendor *</th>
                <th className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold text-left px-3 py-2.5 w-[12%]">Contact</th>
                <th className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold text-left px-3 py-2.5 w-[10%]">Photo</th>
                <th className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold text-right px-3 py-2.5 w-[6%]"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-b border-[#E8E6E1]/60 hover:bg-[#FAFAF8]/40 group" data-testid={`material-row-${r.id}`}>
                  <td className="px-2 py-1.5 align-top">
                    <MaterialSelect
                      value={r.item_name}
                      onChange={(name, material) => onItemChange(r.id, name, material)}
                      projectType={projectType}
                      testId={`material-row-${r.id}-item`}
                    />
                  </td>
                  <td className="px-2 py-1.5 align-top">
                    <InlineText rowId={r.id} field="company" value={r.company} placeholder="Brand" testId={`material-row-${r.id}-company`} />
                  </td>
                  <td className="px-2 py-1.5 align-top">
                    <InlineText rowId={r.id} field="item_code" value={r.item_code} placeholder="Code" testId={`material-row-${r.id}-code`} />
                  </td>
                  <td className="px-2 py-1.5 align-top">
                    <VendorSelect
                      value={r.vendor_name}
                      onChange={(name, vendor) => onVendorChange(r.id, name, vendor)}
                      testId={`material-row-${r.id}-vendor`}
                    />
                  </td>
                  <td className="px-2 py-1.5 align-top">
                    {r.vendor_id ? (
                      <div className="flex items-center gap-1.5 px-2.5 h-9 rounded-lg bg-[#FAFAF8] border border-[#E8E6E1]" title="Auto-filled from Vendor Master · read-only" data-testid={`material-row-${r.id}-phone-readonly`}>
                        <span className="text-xs text-[#2C2C2E] truncate">{r.vendor_phone || '—'}</span>
                      </div>
                    ) : (
                      <InlineText rowId={r.id} field="vendor_phone" value={r.vendor_phone} placeholder="Phone" testId={`material-row-${r.id}-phone`} validate={isValidPhone} />
                    )}
                  </td>
                  <td className="px-2 py-1.5 align-top">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {(r.attachments || []).slice(0, 3).map(a => (
                        <button key={a.id} type="button" onClick={() => setPreviewAttachment(a)}
                          className="relative w-8 h-8 rounded-md overflow-hidden border border-[#E8E6E1] hover:ring-2 hover:ring-[#3A8C8C]/30"
                          data-testid={`material-row-${r.id}-thumb-${a.id}`}>
                          <img src={a.data_url} alt={a.name} className="w-full h-full object-cover" />
                        </button>
                      ))}
                      {(r.attachments || []).length > 3 && (
                        <span className="text-[9px] text-[#6B7280]">+{(r.attachments || []).length - 3}</span>
                      )}
                      <input
                        ref={el => { if (el) fileRefs.current[r.id] = el; }}
                        type="file"
                        accept={ALLOWED_TYPES.join(',')}
                        className="hidden"
                        onChange={e => { onAttach(r.id, e.target.files); e.target.value = ''; }}
                      />
                      <button type="button" onClick={() => fileRefs.current[r.id]?.click()}
                        className="w-8 h-8 rounded-md border border-dashed border-[#D4D0C8] flex items-center justify-center hover:border-[#3A8C8C] hover:bg-[#3A8C8C]/5 transition-colors"
                        title="Upload photo (JPG/PNG/WEBP)"
                        data-testid={`material-row-${r.id}-upload`}>
                        <ImageIcon size={12} className="text-[#6B7280]" />
                      </button>
                    </div>
                  </td>
                  <td className="px-2 py-1.5 align-top text-right">
                    <button onClick={() => deleteRow(r.id)}
                      className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg hover:bg-[#C4727F]/10 transition-all"
                      data-testid={`material-row-${r.id}-delete`}>
                      <Trash2 size={12} className="text-[#C4727F]" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length === 0 && (
            <div className="py-12 text-center">
              <Layers size={28} strokeWidth={1.5} className="text-[#D4D0C8] mx-auto mb-2" />
              <p className="text-xs text-[#6B7280]">No materials yet. Click <span className="font-medium text-[#3A8C8C]">+ Add Row</span> to start.</p>
            </div>
          )}
        </div>
      </div>

      {/* Preview modal - shared component */}
      <AttachmentPreview
        attachment={previewAttachment}
        onClose={() => setPreviewAttachment(null)}
        onRemove={(att) => {
          const row = rows.find(r => (r.attachments || []).some(a => a.id === att.id));
          if (row) removeAttachment(row.id, att.id);
        }}
      />

      {confirmDialog}
    </div>
  );
}
